/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React, { FunctionComponent, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { getAEMResource, getHomepage, getLanguageIso } from '../../selectors/generic';
import { isLoadingById } from '../../selectors/loader';
import { CardsContainer } from '../molecules/cards/CardsContainer';
import ProductGridCard from '../molecules/cards/ProductGridCard';
import { ProductCrossSellComponent } from '../../data/definitions/cross-sell';
import { IHomepage } from '../../interfaces/aem/IHomepage';
import { GTMContextProvider } from '../../analytics/ga4/utils/GTMContext';
import { useI18N } from '../hoc/withI18N';
import { addProductClickEvent } from '../../utils/customEventHandler';
import { selectMyPuratosAccountEmail, selectUserId } from '../../selectors/auth';
import { setProductCrossSell } from '../../actions/analytics';
import { getProductTileData, isProductTileDataLoading } from '../../selectors/products';
import { fetchProductTileData } from '../../actions/products';
import { isLoggedIn } from '../../data/user.model';
import { GA4CrossSellComponentType } from '../../analytics/ga4/types/component';
import { GA4InteractionType } from '../../analytics/ga4/types/interaction';
import { getCurrencyCode } from '../../selectors/cart';
import { googleEvents } from '../../utils/googleEvents';
import { GA4LoginType } from '../../analytics/ga4/types/login';
import { GA4PageType } from '../../analytics/ga4/types/page';
import { productEventParseSelect } from '../../analytics/ga4/parsers/item';

interface IProps {
  homepage: IHomepage;
  resourceLoading: boolean;
  resource?: ProductCrossSellComponent;
  hideAddToCart?: boolean;
  isInModal?: boolean
}

export const ProductSocialProof: FunctionComponent<IProps> = ({
  resource,
  resourceLoading,
  homepage,
  hideAddToCart = false,
  isInModal
}) => {
  const translate = useI18N();
  const userId = useSelector(selectUserId);
  const currency = useSelector(getCurrencyCode);
  const productTileData = useSelector(getProductTileData);
  const dispatch = useDispatch();
  const tileDataLoading = useSelector(isProductTileDataLoading);
  const userEmail = useSelector(selectMyPuratosAccountEmail);
  const crossSellType = isInModal 
    ? GA4CrossSellComponentType.MODAL_SOCIAL_PROOF 
    : GA4CrossSellComponentType.OTHER_CUSTOMERS_BOUGHT;

  const onClickCard = (product, position) => {
    dispatch(
      setProductCrossSell({ ...product, type: crossSellType })
    );
    addProductClickEvent(
      product,
      GA4InteractionType.pdpSocialProof,
      true,
      userId,
      currency,
      position
    );
    googleEvents.selectItem({
      user_id: userId,
      page_language: getLanguageIso(),
      email_address: isLoggedIn() ? userEmail : '',
      login_status: userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
      website_section: GA4PageType.product,
      items: productEventParseSelect(product, currency, GA4InteractionType.pdpSocialProof)
    });
  };

  useEffect(() => {
    if (resource.products) {
      // Currently, we don't want to push impressions on product pages anymore.
      /*dispatch(setProductImpressions({
          products: resource.products,
          list: GA4CrossSellComponentType.OTHER_CUSTOMERS_BOUGHT,
          type: GA4InteractionType.pdpSocialProof
      }));*/
      if (isLoggedIn())
        dispatch(fetchProductTileData({ ids: resource?.products?.map(product => product.id) }));
    }
  }, [resource.products]);

  if (resourceLoading || !resource?.products || resource?.products?.length < 1 || tileDataLoading) {
    return null;
  }

  return (
    <div className="container">
      <div className="row">
        <div className="social-proof container-fluid">
          <div className="row">
            <div className="col-md-12">
              <h4>{translate(resource?.title)}</h4>
            </div>
            <div className={'col-md-12' + (!resource?.buttonVisible ? ' p-margin-bottom-sm' : '')}>
              <div className="row">
                <CardsContainer>
                  {resource?.products?.map((e, index) => (
                    <GTMContextProvider key={e.id} type={GA4InteractionType.pdpSocialProof}>
                      <ProductGridCard
                        item={e}
                        size={'12'}
                        fallback={homepage.fallbackImage}
                        hoverEffect
                        hideOverlay
                        addEvent={onClickCard}
                        tileData={
                          productTileData?.filter(tile => tile.aemProductId === e.id)[0] || []
                        }
                        hideAddToCart={hideAddToCart}
                        position={index + 1}
                      />
                    </GTMContextProvider>
                  ))}
                </CardsContainer>
              </div>
            </div>
            {resource?.buttonVisible && (
              <div className="social-proof__button col-md-12 p-flex p-justify-center p-margin-top-md p-margin-bottom-md">
                <a className="btn-primary" href={resource?.tagLink}>
                  {translate(resource?.buttonText, resource?.productName)}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state, ownProps: any) => ({
    resource: getAEMResource(state, ownProps.model) || {},
    homepage: getHomepage(state),
    resourceLoading: isLoadingById(state, ownProps.model)
  }),
  {}
)(ProductSocialProof);
